let hlsScriptPromise = null;

const getHlsScriptUrl = () => `${process.env.BASE_URL || "/"}hls.js`;

export function isM3u8Url(url) {
  return /\.m3u8(\?|$)/i.test(url || "");
}

function getHlsConstructor() {
  if (window.Hls) {
    return Promise.resolve(window.Hls);
  }
  if (hlsScriptPromise) {
    return hlsScriptPromise;
  }

  const scriptUrl = getHlsScriptUrl();
  hlsScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      const timer = window.setTimeout(() => {
        hlsScriptPromise = null;
        reject(new Error("hls.js 加载超时"));
      }, 8000);
      existingScript.addEventListener("load", () => {
        window.clearTimeout(timer);
        resolve(window.Hls);
      });
      existingScript.addEventListener("error", () => {
        window.clearTimeout(timer);
        hlsScriptPromise = null;
        reject(new Error("hls.js 加载失败"));
      });
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => resolve(window.Hls);
    script.onerror = () => {
      hlsScriptPromise = null;
      reject(new Error("hls.js 加载失败"));
    };
    document.head.appendChild(script);
  });

  return hlsScriptPromise;
}

function playMedia(media) {
  const playPromise = media.play();
  if (playPromise && playPromise.catch) {
    return playPromise.catch(() => {});
  }
  return Promise.resolve();
}

export function destroyVideoPlayer(media, hls) {
  if (hls) {
    hls.destroy();
  }
  if (media) {
    media.pause();
    media.removeAttribute("src");
    media.load();
  }
}

/**
 * m3u8 使用 hls.js 播放，其它格式直接设置 src
 * @returns {Promise<object|null>} hls 实例，非 m3u8 返回 null
 */
export async function playVideoUrl(media, url, onError, autoplay = true) {
  if (!media || !url) {
    return null;
  }

  if (!isM3u8Url(url)) {
    media.src = url;
    if (autoplay) {
      await playMedia(media);
    }
    return null;
  }

  let Hls = window.Hls;
  try {
    Hls = await getHlsConstructor();
  } catch (error) {
    const message = "HLS 播放组件加载失败，请检查网络后重试";
    if (onError) {
      onError(message, error);
    }
    return null;
  }

  if (Hls && Hls.isSupported()) {
    const hls = new Hls({
      enableWorker: true,
    });
    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(url);
    });
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (autoplay) {
        playMedia(media);
      }
    });
    hls.on(Hls.Events.ERROR, (event, data) => {
      if (data && data.fatal) {
        const message = `HLS 播放失败：${
          data.details || data.type || "请检查链接是否有效"
        }`;
        if (onError) {
          onError(message, data);
        }
      }
    });
    hls.attachMedia(media);
    return hls;
  }

  if (media.canPlayType("application/vnd.apple.mpegurl")) {
    media.src = url;
    if (autoplay) {
      await playMedia(media);
    }
    return null;
  }

  if (onError) {
    onError("当前浏览器不支持 HLS 播放");
  }
  return null;
}

/** 兼容 Aliplayer 常用 API，供课程页复用原有事件逻辑 */
export function createVideoPlayerAdapter(media, hls) {
  const adapter = {
    _media: media,
    _hls: hls || null,
    play() {
      return media.play();
    },
    pause() {
      media.pause();
    },
    dispose() {
      destroyVideoPlayer(media, adapter._hls);
      adapter._hls = null;
    },
    getCurrentTime() {
      return media.currentTime || 0;
    },
    getDuration() {
      return media.duration || 0;
    },
    getStatus() {
      return media.paused ? "pause" : "play";
    },
    seek(time) {
      media.currentTime = time;
    },
    setSpeed(rate) {
      media.playbackRate = rate;
    },
    setSpeedOptions() {},
    getVideoElement() {
      return media;
    },
    on(event, handler) {
      const eventMap = {
        ready: "loadedmetadata",
        play: "play",
        pause: "pause",
        ended: "ended",
        ratechange: "ratechange",
      };
      const nativeEvent = eventMap[event];
      if (nativeEvent) {
        media.addEventListener(nativeEvent, handler);
      }
    },
  };

  return adapter;
}
